import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { Field } from 'factorial-form'

import { observer } from 'mobx-react'

import TextInput from 'modules/core/components/Inputs/Text'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useGraphQLClient } from 'modules/core/lib/graphql/provider'
import i18n from 'modules/core/lib/i18n'
import { track } from 'modules/core/lib/metrics'

import Compensation from 'modules/contracts/store/models/Compensation'
import type SupplementForm from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/SupplementForm'
import { getPercentage } from 'modules/payroll_bundle/lib/percentage'
import { roundToDecimals } from 'modules/payroll_bundle/lib/roundToDecimals'
import { useStatusAuthorizations } from 'modules/payroll_bundle/lib/statusAuthorizationsContext'

interface Props {
  form: SupplementForm
  onSubmit?: () => void
  percentage: number
  setPercentage: React.Dispatch<React.SetStateAction<number>>
  initialAmount: number
  setInitialAmount: React.Dispatch<React.SetStateAction<number>>
  isChangeFromAmount: boolean
  setIsChangeFromAmount: React.Dispatch<React.SetStateAction<boolean>>
  hover: boolean
}

const CalculatorPopover = ({
  form,
  onSubmit,
  percentage,
  setPercentage,
  initialAmount,
  setInitialAmount,
  isChangeFromAmount,
  setIsChangeFromAmount,
  hover,
}: Props) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const graphQLClient = useGraphQLClient()
  const isUpTo = form.compensationType === 'up_to'

  const { supplementPermissions } = useStatusAuthorizations()

  const canEditEmployeeSupplements =
    supplementPermissions[String(form.employee?.attributes.id)]?.canEditEmployeeSupplements ?? false

  const [hasAppliedCalculator, setHasAppliedCalculator] = React.useState(false)

  const compensationValue = () => {
    return Compensation.amount(form.compensation, form.employee, locale)
  }

  const compensationAmount = () => form.compensation?.attributes.amount / 100

  useUnsafeEffect(() => {
    form.get('percentage_up_to').set(percentage)
  }, [form, percentage])

  const handleCalculatorChange = (field: Field) => {
    if (field.value < 0) {
      return form.get('amount_in_cents').set(0)
    }

    const PERCENT = 0.01
    const result = field.value * PERCENT * compensationAmount()
    form.get('amount_in_cents').set(roundToDecimals(result))
    setIsChangeFromAmount(false)
    setPercentage(field.value)
  }

  const applyCalculatorResult = () => {
    setHasAppliedCalculator(true)

    if (onSubmit) onSubmit()

    track(graphQLClient, 'Percentage Calculator Applied')
    setTimeout(() => {
      setHasAppliedCalculator(false)
      setInitialAmount(form.get('amount_in_cents').value)
    }, 1000)
  }

  const onOutsideClick = () => {
    if (!hasAppliedCalculator && !isChangeFromAmount) {
      const totalUpToAmount = compensationAmount()

      const percentageValue = roundToDecimals(getPercentage(initialAmount, totalUpToAmount))
      form.get('amount_in_cents').set(initialAmount)
      setPercentage(percentageValue)
    }
  }

  if (isUpTo && canEditEmployeeSupplements) {
    if (!hover) {
      return <Button.Decorative type="secondary" iconName="calculator" />
    }
    return (
      <PopoverMenu type="dialog">
        <PopoverMenu.Trigger>
          <Button.Decorative type="secondary" iconName="calculator" />
        </PopoverMenu.Trigger>
        <PopoverMenu.Content
          side="bottom"
          align="center"
          onInteractOutside={onOutsideClick}
          width="full"
        >
          <Box flexDirection="row" alignItems="center" gap="s16" paddingY="s4" paddingX="s16">
            <Box gap="s8" flexDirection="row" alignItems="center">
              <Box width="s96">
                <TextInput
                  field={form.get('percentage_up_to')}
                  onChange={handleCalculatorChange}
                  indicator="%"
                  getRef={(el) => el?.focus()}
                />
              </Box>
              <Box flexGrow>
                <Text.Normal color="grey700" ellipsis>
                  {i18n.t('common.of')} {compensationValue()}
                </Text.Normal>
              </Box>
            </Box>
            <PopoverMenu.Close>
              <Button type="primary" onClick={applyCalculatorResult}>
                {i18n.t('common.apply')}
              </Button>
            </PopoverMenu.Close>
          </Box>
        </PopoverMenu.Content>
      </PopoverMenu>
    )
  }

  return null
}

export default observer(CalculatorPopover)
